import Image from "next/image";
import { Check } from "lucide-react";
import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";
import PageHero from "@/components/landing-page/PageHero";
import Faq from "@/components/landing-page/home/Faq";
import FinalCta from "@/components/landing-page/home/FinalCta";
import Reveal from "@/components/ui/Reveal";

const features = [
  {
    id: "personalized-learning",
    eyebrow: "Personalized learning",
    title: "A learning path built for every person.",
    body: "inaraX adapts learning to the person, the role and the goal. Every path starts from what someone already knows and keeps adjusting as they progress, so no one wastes time on material they have mastered.",
    points: ["Adapts as each person progresses", "Skips what people already know", "Keeps content current as AI changes"],
    image: { src: "/images/reel/lesson.png", alt: "An inaraX lesson adapted to the learner" },
  },
  {
    id: "ai-assessment",
    eyebrow: "AI assessment & skill mapping",
    title: "See where your workforce stands today.",
    body: "A short AI assessment measures each employee's current AI capability. The results roll up into a skill map by team and role, giving L&D a baseline to plan from and to measure against.",
    points: ["Baseline for every employee", "Skill maps by team and role", "Re-assessment to show progress"],
    image: { src: "/images/reel/placement.png", alt: "The inaraX AI capability assessment" },
  },
  {
    id: "role-based-learning",
    eyebrow: "Role-based learning",
    title: "Learning mapped to each role and level.",
    body: "Finance, sales, engineering and leadership use AI differently. inaraX maps learning to each role and seniority level, with practice drawn from the work people actually do.",
    points: ["Tracks for every function", "Paths by seniority", "Practice on role-specific scenarios"],
    image: { src: "/images/reel/courses.png", alt: "Role-based learning paths in inaraX" },
  },
  {
    id: "expert-validation",
    eyebrow: "Expert validation",
    title: "Skills verified by human experts.",
    body: "AI gives every submission immediate feedback. Industry experts then review projects and assessments against real-world standards, so the skills you report on are verified, not just completed.",
    points: ["Expert review of projects and assessments", "Real-world industry standards", "Credentials you can trust"],
    image: { src: "/images/inarax-certificate.png", alt: "An inaraX expert-validated certificate" },
  },
] as const;

export default function ProductPage() {
  return (
    <LandingPageShell>
      <PageHero
        eyebrow="Product"
        title="Personalized by AI. Validated by human experts."
        description="One platform that assesses your workforce, personalizes learning for every role, and verifies the skills that matter."
      />

      {features.map((feature, index) => (
        <section
          key={feature.id}
          id={feature.id}
          aria-labelledby={`${feature.id}-heading`}
          className={`scroll-mt-20 px-margin-mobile md:px-margin-desktop py-size-xl ${index % 2 === 0 ? "bg-mkt-surface" : "bg-white"}`}
        >
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-size-lg items-center">
            <Reveal className={index % 2 === 1 ? "lg:order-2" : ""}>
              <div className="flex items-center gap-base mb-size-sm">
                <span className="w-12 h-[1px] bg-mkt-primary" />
                <span className="font-label-md text-label-md text-mkt-primary uppercase tracking-widest">
                  {feature.eyebrow}
                </span>
              </div>
              <h2
                id={`${feature.id}-heading`}
                className="font-display-lg text-[clamp(28px,3vw,44px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-inarax-black mb-size-md"
              >
                {feature.title}
              </h2>
              <p className="font-body-lg text-body-lg text-mkt-on-surface-variant mb-size-md">{feature.body}</p>
              <ul className="list-none pl-0 ml-0 flex flex-col gap-size-sm">
                {feature.points.map((point) => (
                  <li key={point} className="flex items-start gap-size-sm font-body-md text-body-md text-mkt-on-surface">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-mkt-primary" />
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delayMs={100}>
              <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-mkt-primary/20 bg-white shadow-[0_4px_16px_rgba(0,60,185,0.10)]">
                <Image
                  src={feature.image.src}
                  alt={feature.image.alt}
                  fill
                  sizes="(min-width: 1024px) 45vw, 100vw"
                  className="object-contain p-size-md"
                />
              </div>
            </Reveal>
          </div>
        </section>
      ))}

      <Faq heading="Frequently asked questions" />
      <FinalCta />
    </LandingPageShell>
  );
}
